import React, { useEffect, useState } from 'react'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { billingQueue as urls } from '@URLS'
import { toast } from '@CommonComponents/toast'
import { InlineBillingFields, getDateErrors } from './InlineBillingFields'
import { CreateInvoiceAccountModal } from './CreateInvoiceAccountModal'

const canConfirm = vals => {
  if (!vals.invoice_account_id || !vals.invoice_type || !vals.billing_price) return false
  if (vals.invoice_type === 'one_time') {
    if (!vals.billing_date) return false
    const { enrollmentError, billingDateError } = getDateErrors(vals)
    if (enrollmentError || billingDateError) return false
    return true
  }
  if (vals.invoice_type === 'recurring') {
    if (!vals.billing_frequency) return false
    if (vals.billing_frequency === 'custom') return !!vals.billing_frequency_custom
    return true
  }
  return false
}

const initValues = br => ({
  school_id: br.school ? br.school.id : '',
  invoice_account_id: br.invoice_account ? br.invoice_account.id : '',
  invoice_type: br.invoice_type || '',
  billing_price: br.billing_price || '',
  billing_date: br.billing_date || '',
  enrollment_start: br.enrollment_start || '',
  enrollment_end: br.enrollment_end || '',
  billing_frequency: br.billing_frequency || '',
  billing_frequency_custom: br.billing_frequency_custom || '',
})

export const QueueItem = ({ br }) => {
  const [state, dispatch] = useStateValue()
  const [values, setValues] = useState(initValues(br))
  const [schoolAccounts, setSchoolAccounts] = useState([])
  const [loadingAction, setLoadingAction] = useState(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)

  const { schools } = state
  const isDeferred = br.status === 'deferred'

  const handleChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }))
  }

  const handleSchoolChange = async schoolId => {
    if (!schoolId) {
      setSchoolAccounts([])
      return
    }
    try {
      const resp = await client.get(urls.getSchoolDetail(schoolId))
      setSchoolAccounts(resp.data.invoice_accounts || [])
    } catch {
      setSchoolAccounts([])
    }
  }

  useEffect(() => {
    if (values.school_id) {
      handleSchoolChange(values.school_id)
    }
  }, [])

  const handleConfirm = async () => {
    setLoadingAction('confirm')
    try {
      await client.post(urls.assignCourse(), {
        billing_record_id: br.id,
        invoice_account_id: values.invoice_account_id,
        invoice_type: values.invoice_type,
        billing_price: values.billing_price,
        billing_date: values.billing_date || null,
        enrollment_start: values.enrollment_start || null,
        enrollment_end: values.enrollment_end || null,
        billing_frequency: values.billing_frequency || null,
        billing_frequency_custom: values.billing_frequency_custom || null,
      })
      dispatch({ type: 'REMOVE_FROM_QUEUE', payload: br.id })
      toast.success(`"${br.course.title}" assigned.`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign course.')
    }
    setLoadingAction(null)
  }

  const handleDefer = async () => {
    setLoadingAction('defer')
    try {
      await client.post(urls.deferCourse(), { billing_record_id: br.id })
      dispatch({ type: 'REMOVE_FROM_QUEUE', payload: br.id })
      toast.success('Course deferred.')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to defer course.')
    }
    setLoadingAction(null)
  }

  const handleNotBillable = async () => {
    setLoadingAction('nb')
    try {
      await client.post(urls.markNotBillable(), { billing_record_id: br.id })
      dispatch({ type: 'REMOVE_FROM_QUEUE', payload: br.id })
      toast.success('Marked as not billable.')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status.')
    }
    setLoadingAction(null)
  }

  const handleReturn = async () => {
    setLoadingAction('return')
    try {
      await client.post(urls.undeferCourse(), { billing_record_id: br.id })
      dispatch({ type: 'REMOVE_FROM_QUEUE', payload: br.id })
      toast.success('Returned to queue.')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to return course.')
    }
    setLoadingAction(null)
  }

  const handleAccountCreated = newAccount => {
    setSchoolAccounts(prev => [...prev, newAccount])
    setValues(prev => ({ ...prev, invoice_account_id: newAccount.id }))
  }

  const dateErrors = getDateErrors(values)
  const ready = canConfirm(values)
  const busy = loadingAction !== null

  return (
    <div className="card-myed mb-2">
      <div className="card-body py-3">
        {/* Header row */}
        <div className="d-flex align-items-start justify-content-between mb-1">
          <div>
            <strong>{br.course.title}</strong>
            {br.is_duplicated && (
              <span className="label label-primary ml-2">
                <i className="fa-regular fa-clone mr-1" />
                Duplicated
                {br.source_course && (
                  <span className="ml-1">from #{br.source_course.coursenum}</span>
                )}
              </span>
            )}
            {!br.is_duplicated && (
              <span className="label label-warning ml-2">New course</span>
            )}
            {isDeferred && (
              <span className="label label-secondary ml-2">Deferred</span>
            )}
          </div>
        </div>

        {/* Meta chips */}
        <div className="d-flex flex-wrap mb-2" style={{ gap: '6px' }}>
          {br.school && (
            <span className="label label-primary">{br.school.name}</span>
          )}
          <span className="label label-default">{br.course.coursenum}</span>
          <span className="label label-default">${br.course.course_fee}</span>
        </div>

        {br.sales_reps && br.sales_reps.length > 0 && (
          <p className="text-muted small mb-2">
            <i className="fa-regular fa-user mr-1" />
            {br.sales_reps.join(', ')}
          </p>
        )}

        {/* Billing form or deferred actions */}
        {isDeferred ? (
          <div className="d-flex align-items-center">
            <span className="text-muted small mr-3">This course has been deferred.</span>
            <button
              className="btn btn-sm btn-myed btn-default btn-default-myed"
              onClick={handleReturn}
              disabled={busy}
            >
              {loadingAction === 'return' ? (
                <i className="fa-regular fa-spinner fa-spin mr-1" />
              ) : (
                <i className="fa-regular fa-undo mr-1" />
              )}
              Return to queue
            </button>
          </div>
        ) : (
          <>
            <InlineBillingFields
              schools={schools}
              schoolAccounts={schoolAccounts}
              values={values}
              onChange={handleChange}
              onSchoolChange={handleSchoolChange}
              onOpenCreateModal={() => setCreateModalOpen(true)}
              errors={dateErrors}
            />
            <CreateInvoiceAccountModal
              show={createModalOpen}
              toggle={setCreateModalOpen}
              defaultSchoolId={values.school_id || null}
              onCreated={handleAccountCreated}
            />

            <div className="d-flex align-items-center justify-content-end mt-2" style={{ gap: '8px' }}>
              <button
                className="btn btn-myed btn-default btn-default-alt-myed btn-sm"
                onClick={handleNotBillable}
                disabled={busy}
              >
                {loadingAction === 'nb' ? (
                  <i className="fa-regular fa-spinner fa-spin mr-1" />
                ) : null}
                Not billable
              </button>
              <button
                className="btn btn-myed btn-default btn-default-myed btn-sm"
                onClick={handleDefer}
                disabled={busy}
              >
                {loadingAction === 'defer' ? (
                  <i className="fa-regular fa-spinner fa-spin mr-1" />
                ) : null}
                Defer
              </button>
              <button
                className={`btn btn-myed ${ready ? 'btn-success btn-success-myed' : 'btn-default btn-default-myed'} btn-sm`}
                onClick={handleConfirm}
                disabled={!ready || busy}
              >
                {loadingAction === 'confirm' ? (
                  <i className="fa-regular fa-spinner fa-spin mr-1" />
                ) : (
                  <i className="fa-regular fa-check mr-1" />
                )}
                Confirm
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
